/**
 * Matriks personil per tahap kerja — K133.
 *
 * Owner: *"sebaiknya langsung disediakan fieldnya masing masing untuk tiap
 * tahapan kerja, masing masing ada field ketua dan field anggota... lalu
 * semuanya itu dikelompokan per region"*.
 *
 * ---------------------------------------------------------------------------
 * ⛔ KENAPA BENTUKNYA DIBALIK DARI LAYAR LAMA
 * ---------------------------------------------------------------------------
 *
 * Layar lama menuntut Kepala Cabang memikirkan *"orang ini tahapnya apa"*.
 * Yang ada di kepalanya *"tahap ini siapa ketuanya"*. Bentuk yang memaksa orang
 * menerjemahkan pertanyaannya sendiri akan diisi separuh, dan separuh yang
 * kosong nol pernah terlihat sebagai kosong.
 *
 * ---------------------------------------------------------------------------
 * ⛔ NOMOR TELEGRAM NOL BISA DIKETIK — DAN INI BUKAN PILIHAN DESAIN
 * ---------------------------------------------------------------------------
 *
 * Owner meminta *"kepala cabang tinggal mengisikan nomor telegramnya"*. Itu
 * MUSTAHIL: bot Telegram nol bisa memulai percakapan, dan nomor telepon BUKAN
 * `chat_id`. Nol ada API yang mengubah satu jadi yang lain.
 *
 * Kalau field itu tetap dibuat: Kepala Cabang mengisi `081…`, layarnya terlihat
 * lengkap, semua tahap punya ketua — dan NOL SATU PESAN PUN pernah sampai.
 * Kendali mati yang membuat lampu jadi hijau.
 *
 * Yang dipasang sebagai gantinya: nomor HP disimpan sebagai data karyawan, dan
 * tombol **Tautan Telegram** menerbitkan tautan yang WAJIB diketuk orangnya
 * sendiri. Layar menandai siapa yang belum mengetuk — karena orang yang belum
 * mendaftar NOL AKAN PERNAH menerima tugas.
 *
 * ---------------------------------------------------------------------------
 * PENGECUALIAN 8 (Owner 3 Nov 2026) — baris Jahit & Gudang Barang Jadi punya kotak
 * ---------------------------------------------------------------------------
 *
 * K230: penerima serah terima lewat Telegram = KETUA tahap itu di layar ini, untuk SEMUA tahap penerima. Dulu baris
 * Jahit tanpa kotak dan Gudang Barang Jadi tidak tampil, jadi serah terima Potong→Jahit dan QC/Packing→Gudang tidak
 * punya penerima sah. Sekarang tiap baris menyebut arti Ketuanya (tugas harian / serah terima) dari penanda backend
 * (`lib/personilMatriks.js`); tugas harian Jahit tetap ke ketua group (bagian Group Jahit).
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  apiErrorMessage,
  getMatriksPersonil,
  getRegions,
  isiSlotPersonil,
  labelTahap,
  tautanTelegram,
} from "@/lib/papanKerjaApi";
import { useAuth } from "@/contexts/AuthContext";
import GroupJahit from "@/components/GroupJahit";
import Hint from "@/components/etams/Hint";
import { artiKetua, keteranganBaris, ringkasTanpaKetua } from "@/lib/personilMatriks";
import { pesanPendaftaranTelegram } from "@/lib/telegramDaftar";

function Lencana({ nada, children }) {
  const warna = {
    merah: "border-danger-soft-border bg-danger-soft text-danger-soft-foreground",
    kuning: "border-warning-soft-border bg-warning-soft text-warning-soft-foreground",
    hijau: "border-success-soft-border bg-success-soft text-success-soft-foreground",
    abu: "border-border bg-muted text-muted-foreground",
  }[nada];
  return (
    <span className={`whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium ${warna}`}>
      {children}
    </span>
  );
}

/** Satu kotak: tahap × peran. */
function Kotak({ tahap, peran, isi, orang, onIsi, onTautan, sibuk }) {
  const [tambah, setTambah] = useState(null);

  if (tambah) {
    return (
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-muted p-2">
        <input
          autoFocus
          placeholder="Nama lengkap"
          className="w-40 rounded border border-border px-2 py-1 text-sm"
          value={tambah.full_name}
          onChange={(e) => setTambah({ ...tambah, full_name: e.target.value })}
        />
        <input
          placeholder="Nomor HP"
          className="w-32 rounded border border-border px-2 py-1 text-sm"
          value={tambah.phone}
          onChange={(e) => setTambah({ ...tambah, phone: e.target.value })}
        />
        <button
          type="button"
          disabled={sibuk || !tambah.full_name.trim()}
          onClick={() => {
            onIsi({ peran, orang_baru: tambah });
            setTambah(null);
          }}
          className="rounded bg-primary px-3 py-1 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          Simpan
        </button>
        <button
          type="button"
          onClick={() => setTambah(null)}
          className="rounded border border-border px-2 py-1 text-sm text-muted-foreground"
        >
          Batal
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        className="w-48 rounded border border-border px-2 py-1 text-sm"
        data-testid={`kotak-${tahap.code}-${peran}`}
        value={isi?.employee_id || ""}
        disabled={sibuk}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "__baru__") return setTambah({ full_name: "", phone: "" });
          onIsi(v ? { peran, employee_id: v } : { peran, kosongkan: true });
        }}
      >
        <option value="">— kosong —</option>
        {orang.map((o) => (
          <option key={o.id} value={o.id}>{o.nama}</option>
        ))}
        {/* K133: menambah orang baru TANPA berpindah layar. Ia di bawah daftar,
            bukan di atas — supaya nama yang sudah ada terbaca lebih dulu, dan
            karyawan kembar lahir karena orang nol sempat melihat daftarnya. */}
        <option value="__baru__">+ orang baru…</option>
      </select>

      {isi && (
        <>
          {/* ⛔ Yang belum mendaftar NOL AKAN PERNAH menerima tugas, dan nol
              ada percobaan ulang yang mengubahnya. Yang kurang tindakan
              ORANGNYA — dan ini satu-satunya tempat itu terlihat. */}
          {isi.telegram_terdaftar ? (
            <Lencana nada="hijau">Telegram aktif</Lencana>
          ) : (
            <>
              <Lencana nada="merah">Telegram belum</Lencana>
              <button
                type="button"
                onClick={() => onTautan(isi)}
                className="rounded border border-border px-2 py-1 text-xs font-medium text-foreground hover:bg-muted"
              >
                Tautan
              </button>
            </>
          )}
          {/* K127 + K133: rangkap SAH, tapi angkanya wajib terlihat. Mengisi
              enam kotak dengan nama yang sama cuma butuh enam ketukan, dan nol
              ada satu layar pun yang menjumlahkannya. */}
          {isi.jumlah_tahap > 2 && (
            <Lencana nada="kuning">{isi.jumlah_tahap} tahap</Lencana>
          )}
        </>
      )}
    </div>
  );
}

export default function PersonilMatriks() {
  const { profile } = useAuth();
  const regionSendiri = profile?.region_id || null;
  const perluPilih = !regionSendiri;

  const [regions, setRegions] = useState([]);
  const [pilihRegion, setPilihRegion] = useState("");
  const regionId = regionSendiri || pilihRegion || undefined;

  const [data, setData] = useState(null);
  const [muat, setMuat] = useState(false);
  const [galat, setGalat] = useState("");
  const [sibuk, setSibuk] = useState(false);
  const [tautan, setTautan] = useState(null);

  useEffect(() => {
    if (!perluPilih) return;
    getRegions()
      .then(({ data: d }) => {
        const daftar = d || [];
        setRegions(daftar);
        if (daftar.length === 1) setPilihRegion(daftar[0].id);
      })
      .catch((e) => setGalat(apiErrorMessage(e)));
  }, [perluPilih]);

  const muatData = useCallback(async () => {
    // ⚠ Berhenti SEBELUM memanggil. Permintaan tanpa wilayah dijawab 400, dan
    //   menampilkannya sebagai kotak merah membuat keadaan "belum memilih"
    //   terlihat seperti kerusakan.
    if (!regionId) {
      setData(null);
      return;
    }
    setMuat(true);
    setGalat("");
    try {
      const { data: d } = await getMatriksPersonil({ region_id: regionId });
      setData(d);
    } catch (e) {
      setGalat(apiErrorMessage(e));
      setData(null);
    } finally {
      setMuat(false);
    }
  }, [regionId]);

  useEffect(() => {
    muatData();
  }, [muatData]);

  const isiSlot = async (stageCode, body) => {
    setSibuk(true);
    setGalat("");
    try {
      await isiSlotPersonil(stageCode, body, { region_id: regionId });
      await muatData();
    } catch (e) {
      setGalat(apiErrorMessage(e));
    } finally {
      setSibuk(false);
    }
  };

  const mintaTautan = async (isi) => {
    setGalat("");
    setTautan(null);
    try {
      const { data: d } = await tautanTelegram(isi.employee_id);
      setTautan({ nama: d.nama, url: d.tautan, jam: d.berlaku_jam, perintah: d.perintah_cadangan });
    } catch (e) {
      setGalat(apiErrorMessage(e));
    }
  };

  // PENGECUALIAN 8: ikut menghitung Jahit (penerima dari Potong) dan Gudang Barang Jadi (penerima dari QC/Packing).
  const tanpaKetua = ringkasTanpaKetua(data?.tahap || []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Personil per Tahap Kerja</h1>
          <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1">
            Tiap tahap punya satu ketua dan satu anggota.
            <Hint>Staf di sini tidak perlu akun login — mereka menerima tugas, melaporkannya, dan mencatat serah terima barang lewat Telegram. Serah terima lewat Telegram hanya dicatat Ketua; bila Ketua berhalangan, Admin Produksi mencatatnya di layar Serah Terima Tahap.</Hint>
          </p>
        </div>
        {perluPilih && (
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-foreground">Wilayah</span>
            <select
              className="rounded-lg border border-border px-3 py-2 text-sm"
              value={pilihRegion}
              onChange={(e) => setPilihRegion(e.target.value)}
            >
              <option value="">Pilih wilayah…</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {galat && (
        <div role="alert" className="rounded-lg border border-danger-soft-border bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground">
          {galat}
        </div>
      )}

      {!regionId && (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Pilih wilayah lebih dulu — tiap wilayah punya timnya sendiri, dan
          orangnya tidak boleh bercampur.
        </div>
      )}

      {/* ⛔ Tahap tanpa ketua adalah informasi yang paling perlu terlihat.
          Tugas hariannya nol punya penanggung jawab. */}
      {data && tanpaKetua.jumlah > 0 && (
        <div className="rounded-xl border border-warning-soft-border bg-warning-soft p-4 text-sm text-warning-soft-foreground"
             data-testid="peringatan-tanpa-ketua">
          <p className="font-semibold">{tanpaKetua.jumlah} tahap belum punya ketua</p>
          <p className="mt-1">
            {tanpaKetua.nama.join(", ")} — {tanpaKetua.kalimat}
          </p>
        </div>
      )}

      {tautan && (
        <div className="rounded-xl border border-border bg-card p-4 text-sm">
          <p className="font-medium text-foreground">
            Tautan pendaftaran Telegram untuk {tautan.nama}
          </p>
          <p className="mt-1 text-muted-foreground">
            Salin seluruh pesan di bawah dan kirim ke orangnya lewat WhatsApp. <strong>Ia sendiri yang harus
            mengetuk tautannya</strong> — tidak bisa digantikan siapa pun. Bila bot hanya membalas petunjuk, ia
            menyalin baris /daftar ke bot. Berlaku {tautan.jam} jam.
          </p>
          {/* T114: tautan + cadangan /daftar dalam satu pesan siap kirim. */}
          <textarea
            readOnly
            rows={5}
            value={pesanPendaftaranTelegram(tautan)}
            onFocus={(e) => e.target.select()}
            data-testid="pesan-pendaftaran-telegram"
            className="mt-2 w-full rounded-lg border border-border bg-muted px-3 py-2 font-mono text-xs"
          />
        </div>
      )}

      {muat && <p className="text-sm text-muted-foreground">Memuat…</p>}

      {/* ⛔ Group jahit DI ATAS tabel, dan itu disengaja: baris JAHIT di tabel
          menunjuk ke sini, jadi tempatnya wajib sudah terlihat saat orang
          membacanya. Menaruhnya di bawah membuat rujukan itu menunjuk ke
          sesuatu yang harus dicari. */}
      {data && <GroupJahit regionId={regionId} orang={(data.orang || []).map((o) => ({ id: o.id, full_name: o.nama }))} onBerubah={muatData} setGalat={setGalat} />}

      {data && (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Tahap</th>
                <th className="px-4 py-3">Ketua</th>
                <th className="px-4 py-3">Anggota (cadangan)</th>
              </tr>
            </thead>
            <tbody>
              {/* ⛔ SEMUA tahap tampil, termasuk yang kosong. Sejak PENGECUALIAN 8 baris Jahit dan Gudang
                  Barang Jadi punya kotak Ketua/Anggota (K230: Ketua = penerima serah terima lewat Telegram); arti
                  Ketua tiap baris ditulis di bawah nama tahapnya supaya tidak tertukar dengan ketua group. */}
              {(data.tahap || []).map((t) => (
                <tr key={t.code} className="border-b border-border last:border-0 align-top"
                    data-testid={`baris-tahap-${t.code}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{labelTahap(t.code)}</div>
                    {artiKetua(t) && (
                      <div className="mt-0.5 max-w-xs text-xs text-muted-foreground">{artiKetua(t)}</div>
                    )}
                    {keteranganBaris(t) && (
                      <div className="mt-0.5 max-w-xs text-xs text-muted-foreground"
                           data-testid={`keterangan-${t.code}`}>{keteranganBaris(t)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Kotak
                      tahap={t} peran="KETUA" isi={t.ketua}
                      orang={data.orang} sibuk={sibuk}
                      onIsi={(b) => isiSlot(t.code, b)}
                      onTautan={mintaTautan}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Kotak
                      tahap={t} peran="ANGGOTA" isi={t.anggota}
                      orang={data.orang} sibuk={sibuk}
                      onIsi={(b) => isiSlot(t.code, b)}
                      onTautan={mintaTautan}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
